import { useWorkspace } from './workspaceContext';
import { useCallback, useDeferredValue, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import * as RadixDialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Bike, CornerDownLeft, Languages, Route, Search, User, type LucideIcon } from 'lucide-react';
import { adminCustomerController, adminVehicleController } from '@/api/generated/endpoints';
import { unwrap } from '@/api/http';
import { errorMessage } from '@/api/errors';
import { routeResources } from '@/app/registry';
import { sections } from '@/app/sections';
import { useAuth } from '@/auth/AuthProvider';
import { customerPath } from '@/components/CustomerLink';
import { LANGUAGES, setLanguage } from '@/i18n';
import { useNavTitle } from '@/i18n/useNavTitle';
import { maskPhone } from '@/lib/format';
import { Kbd, Spinner, cn, toast, useListNavigation } from '@/ui';
import { parseSearch } from './search';

interface Item {
  key: string;
  group: 'jump' | 'customers' | 'screens' | 'actions';
  title: string;
  hint?: string;
  icon: LucideIcon;
  run(): void;
}

/** Most customers a lookup lists; more means the agent should refine the search. */
const CUSTOMER_LIMIT = 6;

/**
 * Global search (/ or ⌘K): finds a customer by phone or email, jumps to a ride, customer or vehicle
 * by number, or opens any screen by name. ↑/↓ and Enter pick; Esc closes.
 */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange(open: boolean): void }) {
  const { t, i18n } = useTranslation('shell');
  const navTitle = useNavTitle();
  const navigate = useNavigate();
  const workspace = useWorkspace();
  const { permissions } = useAuth();
  const [query, setQuery] = useState('');
  const deferred = useDeferredValue(query);
  const intent = useMemo(() => parseSearch(deferred), [deferred]);
  const listId = useId();

  const canCustomers = permissions.canView('customer');
  const lookup = intent.email ? { email: intent.email } : intent.phone ? { phone: intent.phone.phone, countryCode: intent.phone.countryCode } : null;
  const customers = useQuery({
    queryKey: ['search', 'customers', lookup],
    queryFn: async () => unwrap(await adminCustomerController.search({ search: { ...lookup, pagination: { page: 0, pageSize: CUSTOMER_LIMIT } } })),
    enabled: open && canCustomers && lookup !== null,
    staleTime: 60_000,
  });

  const close = () => {
    onOpenChange(false);
    setQuery('');
  };
  const go = (to: string) => {
    close();
    navigate(to);
  };

  const findVehicle = async (vehicleNumber: number) => {
    close();
    try {
      const page = unwrap(await adminVehicleController.search({ search: { vehicleNumber, cursor: null, pageSize: 1 } }));
      const id = page?.content?.[0]?.id;
      if (id != null) navigate(`/operations/vehicles/${id}`);
      else toast.error(t('search.noVehicle', { number: vehicleNumber }));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  // jump targets first, then customers found, then screens
  const items: Item[] = [];
  if (intent.id !== undefined) {
    const n = intent.id;
    const vehicle: Item | null = permissions.canView('vehicle')
      ? { key: 'vehicle', group: 'jump', title: t('search.vehicle', { id: n }), hint: t('search.byVehicleNumber'), icon: Bike, run: () => void findVehicle(n) }
      : null;
    if (vehicle && intent.vehicleFirst) items.push(vehicle);
    if (permissions.canView('ride')) items.push({ key: 'ride', group: 'jump', title: t('search.ride', { id: n }), icon: Route, run: () => go(`/operations/rides/${n}`) });
    if (canCustomers) items.push({ key: 'customer', group: 'jump', title: t('search.customer', { id: n }), icon: User, run: () => go(customerPath(n)) });
    if (vehicle && !intent.vehicleFirst) items.push(vehicle);
  }
  for (const c of lookup ? (customers.data?.content ?? []) : []) {
    if (c.id == null) continue;
    const id = c.id;
    items.push({
      key: `c-${id}`,
      group: 'customers',
      title: c.fullName || t('search.customer', { id }),
      hint: [c.phone != null ? `+${c.countryCode ?? 1} ${maskPhone(c.phone)}` : null, c.email].filter(Boolean).join(' · '),
      icon: User,
      run: () => go(customerPath(id)),
    });
  }
  for (const s of sections) {
    for (const r of s.routes) {
      if (r.hideInMenu || !permissions.canViewAny(routeResources(r))) continue;
      const [section, screen] = [navTitle(s.title), navTitle(r.title)];
      if (intent.text && !`${section} ${screen}`.toLowerCase().includes(intent.text)) continue;
      items.push({ key: r.path, group: 'screens', title: screen, hint: section, icon: s.icon, run: () => go(workspace?.href(r.path) ?? r.path) });
    }
  }
  // "Switch language": found by that phrase or by a language's own name
  for (const l of LANGUAGES) {
    if (!intent.text || l.code === i18n.resolvedLanguage) continue;
    const phrase = t('search.switchLanguage');
    if (!`${phrase} ${l.name} language`.toLowerCase().includes(intent.text)) continue;
    items.push({ key: `lang-${l.code}`, group: 'actions', title: `${phrase}: ${l.name}`, icon: Languages, run: () => (close(), void setLanguage(l.code)) });
  }

  const idOf = useCallback((i: number) => `${listId}-${i}`, [listId]);
  const nav = useListNavigation({ count: items.length, onPick: (i) => items[i]?.run(), idOf, resetKey: `${open}:${deferred}` });

  const groups: { name: Item['group']; items: { item: Item; index: number }[] }[] = [];
  items.forEach((item, index) => {
    const last = groups.at(-1);
    if (last?.name === item.group) last.items.push({ item, index });
    else groups.push({ name: item.group, items: [{ item, index }] });
  });
  const searching = customers.isFetching && lookup !== null;

  return (
    <RadixDialog.Root open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-navy-ink/40" />
        <RadixDialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[10vh] z-50 flex max-h-[70vh] w-[calc(100vw-2rem)] max-w-[640px] -translate-x-1/2 flex-col overflow-hidden rounded-xl border border-line bg-surface font-sans text-ink shadow-pop outline-none"
        >
          <RadixDialog.Title className="sr-only">{t('search.title')}</RadixDialog.Title>
          <div className="flex items-center gap-2.5 border-b border-line px-4">
            <Search size={18} aria-hidden className="shrink-0 text-ink-faint" />
            <input
              autoFocus
              role="combobox"
              aria-label={t('search.title')}
              aria-expanded
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={nav.activeId}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={nav.onKeyDown}
              placeholder={t('search.placeholder')}
              className="m-0 h-12 w-full border-0 bg-transparent p-0 font-sans text-lg text-ink outline-none placeholder:text-ink-faint"
            />
            {searching && <Spinner size={16} label={t('searching', { ns: 'common' })} />}
            <Kbd keys="Escape" />
          </div>
          <div id={listId} role="listbox" aria-label={t('search.results')} className="min-h-0 flex-1 overflow-y-auto p-2">
            {items.length === 0 && (
              <p className="m-0 px-3 py-8 text-center text-sm text-ink-muted">{searching ? t('searching', { ns: 'common' }) : customers.isError ? errorMessage(customers.error) : t('search.empty')}</p>
            )}
            {groups.map((g) => (
              <div key={g.name} role="group" aria-label={t(`search.groups.${g.name}`)} className="mb-1 last:mb-0">
                <div className="px-3 pb-1 pt-2 text-xs font-bold uppercase tracking-wide text-ink-muted" aria-hidden>
                  {t(`search.groups.${g.name}`)}
                </div>
                {g.items.map(({ item, index }) => {
                  const Icon = item.icon;
                  const active = index === nav.active;
                  return (
                    <div
                      key={item.key}
                      id={idOf(index)}
                      role="option"
                      aria-selected={active}
                      onPointerMove={() => nav.setActive(index)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={item.run}
                      className={cn('flex h-10 cursor-pointer items-center gap-3 rounded-md px-3 text-base', active && 'bg-accent-soft')}
                    >
                      <Icon size={16} aria-hidden className={cn('shrink-0', active ? 'text-accent' : 'text-ink-faint')} />
                      <span className="truncate font-bold">{item.title}</span>
                      {item.hint && <span className="truncate text-sm text-ink-muted">{item.hint}</span>}
                      <span className="ml-auto shrink-0 text-ink-faint" aria-hidden>
                        {active ? <CornerDownLeft size={14} /> : <ArrowRight size={14} className="opacity-0" />}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
